import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Package, Building2, Loader2, Lock, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import Sidebar from '../components/Sidebar';
import TradosVerifiedRibbon from '../components/TradosVerifiedRibbon';
import DirectoryWorldMap from '../components/DirectoryWorldMap';
import DirectoryCountryPanel from '../components/DirectoryCountryPanel';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

/** Logo that only replaces the placeholder once the image actually decodes, so a
 *  website URL stored in logo_url never shows as a broken image. */
const CardLogo = ({ company }) => {
  const [ok, setOk] = useState(false);
  const src = typeof company.logo_url === 'string' && /^https?:\/\//i.test(company.logo_url.trim())
    ? company.logo_url : null;
  return (
    <>
      {!ok && <Building2 className="w-6 h-6 text-gray-300" />}
      {src && (
        <img
          src={src}
          alt={company.name}
          onLoad={() => setOk(true)}
          className={`w-full h-full object-contain ${ok ? '' : 'hidden'}`}
        />
      )}
    </>
  );
};

const CompanyCard = ({ company, authed }) => {
  const commodities = company.commodities || [];
  return (
    <Link
      to={`/directory/${company.slug}`}
      className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col"
    >
      <TradosVerifiedRibbon verification={company.verification} size="sm" />
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-3 pr-20">
          <div className="w-12 h-12 rounded-xl border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0"
               style={{ background: company.logo_bg || '#F3F4F6' }}>
            <CardLogo company={company} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-800 truncate">{company.name}</h3>
            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" />
              {[company.city, company.country].filter(Boolean).join(', ') || 'Location N/A'}
            </p>
          </div>
        </div>

        {commodities.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {commodities.slice(0, 4).map((c, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full bg-blue-50 text-[11px] font-medium" style={{ color: BRAND_BLUE }}>
                {c}
              </span>
            ))}
            {commodities.length > 4 && (
              <span className="px-2 py-0.5 rounded-full bg-gray-50 text-[11px] text-gray-500">+{commodities.length - 4}</span>
            )}
          </div>
        )}

        {company.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{company.description}</p>
        )}

        <div className="mt-auto pt-3 border-t border-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Lock className="w-3 h-3" /> {authed ? 'Upgrade your membership to view details' : 'Login to view contact'}
          </span>
          <span className="text-sm font-medium" style={{ color: BRAND_BLUE }}>View details →</span>
        </div>
      </div>
    </Link>
  );
};

const DirectorySearch = () => {
  const { token } = useAuth();
  // Use token (sync from localStorage) not user (loads async) — otherwise the
  // first render shows PublicLayout, then flashes to Sidebar once /auth/me
  // resolves. Logged-in users go inside the admin shell.
  const Shell = token ? Sidebar : PublicLayout;
  useEffect(() => { document.title = 'Trading Commodities Company Directory | Trados'; }, []);
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [commodity, setCommodity] = useState('');
  const [filters, setFilters] = useState({ countries: [], commodities: [] });
  const [countryCounts, setCountryCounts] = useState([]);
  const [results, setResults] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get('/public/directory/filters')
      .then(res => setFilters(res.data))
      .catch(() => {});
    // country counts for the panel below the map (already sorted count-desc)
    api.get('/public/directory/map-data')
      .then(res => setCountryCounts(res.data.countries || []))
      .catch(() => {});
  }, []);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 24 };
      if (q) params.q = q;
      if (country) params.country = country;
      if (commodity) params.commodity = commodity;
      const res = await api.get('/public/directory/search', { params });
      setResults(res.data);
    } catch (e) {
      setResults({ items: [], total: 0, page: 1, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [q, country, commodity, page]);

  // Debounce search-term changes; immediate for filters/page
  useEffect(() => {
    const t = setTimeout(fetchResults, 300);
    return () => clearTimeout(t);
  }, [fetchResults]);

  const resetPageAnd = (setter) => (val) => { setPage(1); setter(val); };

  return (
    <Shell>
      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <div className="rounded-3xl border-2 shadow-sm bg-gradient-to-b from-[#FFFDE7] to-white overflow-hidden" style={{ borderColor: '#E5D9A8' }}>
          <div className="px-4 md:px-8 py-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-600 mb-4">
              <Globe className="w-3.5 h-3.5" style={{ color: BRAND_BLUE }} />
              The world's trading commodities company directory
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Find trading commodities companies worldwide</h1>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
              Search thousands of importers, exporters, traders and brokers. Create a free Trados account to view contact details and reach out.
            </p>

            {/* Animated world map with headline stats */}
            <DirectoryWorldMap />

            {/* Records per country — same shared panel as the admin data view */}
            <div className="mt-6 max-w-5xl mx-auto">
              <DirectoryCountryPanel
                countries={countryCounts}
                selected={country}
                onSelect={(c) => resetPageAnd(setCountry)(c)}
              />
            </div>

            {/* Search bar below the map */}
            <div className="mt-8 max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm p-2 flex flex-col md:flex-row gap-2">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => resetPageAnd(setQ)(e.target.value)}
                  placeholder="Search by company, commodity or city..."
                  className="w-full py-2.5 outline-none text-sm"
                />
              </div>
              <select
                value={country}
                onChange={(e) => resetPageAnd(setCountry)(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 bg-white"
              >
                <option value="">All countries</option>
                {filters.countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={commodity}
                onChange={(e) => resetPageAnd(setCommodity)(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 bg-white"
              >
                <option value="">All commodities</option>
                {filters.commodities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            {loading ? 'Searching...' : `${results.total.toLocaleString()} companies`}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
          </div>
        ) : results.items.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500">No companies found. Try a different search.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.items.map((c) => <CompanyCard key={c.id} company={c} authed={!!token} />)}
            </div>

            {results.total_pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">Page {results.page} of {results.total_pages}</span>
                <button
                  disabled={page >= results.total_pages}
                  onClick={() => setPage((p) => p + 1)}
                  className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
};

export default DirectorySearch;
